import { computed, inject, Injectable } from '@angular/core';

import { patchState, signalState } from '@ngrx/signals';

import { SubmissionStatuses, SubmissionStatusSignalState } from '../../types';
import { DashboardService } from '@data-access/dash-board/services/dashboard.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { delay, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';

export const SUBMISSION_STATUS_SIGNAL_STATE =
  signalState<SubmissionStatusSignalState>({
    error: null,
    isLoading: false,
    data: null,
  });

@Injectable()
export class SubmissionStatusesSignalState {
  private readonly _sumbissionSignalState = SUBMISSION_STATUS_SIGNAL_STATE;
  private readonly _dashboardService = inject(DashboardService);

  readonly $submissionStatuses = computed<SubmissionStatuses | null>(() =>
    this._sumbissionSignalState.data(),
  );
  readonly $isLoading = computed(() => this._sumbissionSignalState.isLoading());
  readonly $error = computed(() => this._sumbissionSignalState.error());

  /**
   * Loads the submission-status buckets into state.
   *
   * @param useMock - Forwarded to `DashboardService.getSubmissionStatuses`;
   * when true the service serves its typed mock payload instead of calling
   * the API. Driven by the card component's `mockToggle` input. Omit or pass
   * false for a real fetch.
   */
  getSubmissionStatuses = rxMethod<boolean | void>(
    pipe(
      tap(() =>
        patchState(this._sumbissionSignalState, {
          isLoading: true,
          error: null,
        }),
      ),
      switchMap((useMock) =>
        this._dashboardService.getSubmissionStatuses(useMock === true).pipe(
          delay(250),
          tapResponse({
            error: (error: Error | null) =>
              patchState(this._sumbissionSignalState, {
                error: error,
                isLoading: false,
              }),
            next: (data) =>
              patchState(this._sumbissionSignalState, {
                data: data,
                isLoading: false,
              }),
          }),
        ),
      ),
    ),
  );

  resetSubmissionState() {
    patchState(this._sumbissionSignalState, {
      data: null,
      isLoading: false,
      error: null,
    });
  }

  loadErrorState() {
    patchState(this._sumbissionSignalState, {
      isLoading: false,
      error: new Error('Error State Loading'),
    });
  }

  loadEmptyState() {
    patchState(this._sumbissionSignalState, {
      isLoading: false,
      error: null,
      data: null,
    });
  }

  loadLoadingState() {
    patchState(this._sumbissionSignalState, {
      isLoading: true,
      error: null,
      data: null,
    });
  }
}
